import { Link } from 'react-router-dom';
import { useCompare } from '@contexts/CompareContext';

/**
 * CompareBar — sticky bottom bar showing selected compare items
 *
 * Appears when 1+ products are in the compare list (max 3).
 * Shows product thumbnails, names, a remove (x) per item, and
 * "Compare now" button (enabled at 2+). "Clear all" link.
 *
 * Sits at the bottom of the viewport, above the footer.
 */
export default function CompareBar() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  if (compareList.length === 0) return null;

  const canCompare = compareList.length >= 2;
  const slotsRemaining = 3 - compareList.length;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-carbon-black text-white shadow-2xl border-t-2 border-electric-blue">
      <div className="sandvik-container py-3">
        <div className="flex items-center gap-4">

          {/* Label */}
          <div className="flex-shrink-0">
            <p className="text-sm font-normal font-sandvik-text">
              Compare ({compareList.length}/3)
            </p>
            {slotsRemaining > 0 && (
              <p className="text-[10px] text-n4 font-sandvik-text">
                {slotsRemaining} more slot{slotsRemaining > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Selected products */}
          <div className="flex-1 flex items-center gap-3 overflow-x-auto">
            {compareList.map((item) => (
              <div
                key={item.itemCode}
                className="flex items-center gap-2 bg-white/10 rounded px-3 py-1.5 flex-shrink-0"
              >
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt=""
                    className="w-8 h-8 object-contain bg-white rounded"
                  />
                ) : (
                  <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center">
                    <span className="material-symbols-outlined text-sm text-n4">image</span>
                  </div>
                )}
                <span className="text-xs font-sandvik-text max-w-[120px] truncate">
                  {item.description || item.itemCode}
                </span>
                <button
                  onClick={() => removeFromCompare(item.itemCode)}
                  className="p-0.5 hover:bg-white/20 rounded transition-colors"
                  aria-label={`Remove ${item.description} from compare`}
                >
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            ))}

            {/* Empty slots */}
            {Array.from({ length: slotsRemaining }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="w-[140px] h-[44px] border border-dashed border-white/20 rounded flex items-center justify-center flex-shrink-0"
              >
                <span className="text-[10px] text-n4 font-sandvik-text">+ Add product</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={clearCompare}
              className="text-xs text-n4 hover:text-white transition-colors font-sandvik-text underline"
            >
              Clear all
            </button>
            {canCompare ? (
              <Link
                to="/compare"
                className="px-5 py-2 bg-electric-blue hover:bg-eb-3 text-white text-sm font-normal font-sandvik-text rounded-none transition-colors"
              >
                Compare now
              </Link>
            ) : (
              <span className="px-5 py-2 bg-n5 text-n3 text-sm font-normal font-sandvik-text rounded-none cursor-not-allowed">
                Compare now
              </span>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
